import React from 'react';
import { v4 as uuid } from 'uuid';
import { useRouter } from 'next/router';
import Head from 'next/head';
import PropTypes from 'prop-types';
import GalleryLayout from 'layouts/GalleryLayout';
import PageTitle from 'components/PageTitle';
import ImageSlider, { Props as SliderProps } from 'components/ImageSlider';
import Button from 'components/Button';
import IllustrationCategorySection from 'components/IllustrationCategorySection';
import useIsMobile from 'hooks/useIsMobile';
import { getEnv } from 'utils';
import capitalizeString from 'utils/capitalizeString';
import normaliseText from 'utils/normaliseText';
import { Typography, Box, Link } from '@mui/material';
import IllustrationThumb, { Props as ThumbProps } from 'components/IllustrationThumb';

import {
  RegisterCard,
  RegisterHeader,
  RegisterHeaderInner,
  RegisterTitle,
  Grid,
  RelatedSection,
  RelatedTitle,
  RelatedGrid,
  Divider,
  DescriptionIllustration,
  MainDividerIllustration,
  LoginForMobile,
  MainLoginForMobile,
  RegisterTitleMobile,
  ButtonSignUpMobile,
} from './styles';
import FlagsSelect from 'components/FlagsSelect';
import { setLocale } from 'utils/locale';

import en from './locales/en';
import ja from './locales/en';
import zh from './locales/en';
import es from './locales/es';
import fr from './locales/fr';
import it from './locales/it';
import { useAuthUser } from 'features/auth/AuthUserContext';
import EmailFormVariant from 'components/EmailFormVariant';

const DEFAULT_PROPS = {
  related: undefined,
};

const Props = {
  data: PropTypes.shape({
    title: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  related: PropTypes.arrayOf(PropTypes.shape(ThumbProps)),
  relatedTitle: PropTypes.string.isRequired,
  sliderImages: SliderProps.images,
  sliderThumbsPosition: SliderProps.thumbsPosition,
  sliderInitialImage: PropTypes.number,
  onTagClick: PropTypes.func.isRequired,
  isTemplatePage: PropTypes.bool,
  descriptionIllustration: PropTypes.string,
};

export default function GalleryDetailPage({
  data,
  related,
  relatedTitle,
  sliderImages,
  sliderThumbsPosition,
  sliderInitialImage,
  onTagClick,
  isTemplatePage,
  descriptionIllustration,
}) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const { isAuthenticated, login } = useAuthUser();
  const { locale } = router;
  const t = setLocale(locale, { en, es, fr, it, ja, zh });

  const schemaData = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://mindthegraph.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Illustrations',
        item: 'https://mindthegraph.com/illustrations',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${data.title}`,
        item: `${getEnv('MTG_URL_APP')}${router.asPath}`,
      },
    ],
  };

  const schemaDataImageObject = {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    name: `${capitalizeString(normaliseText(data.title))} - Edit and Customize Icons of ${capitalizeString(normaliseText(data.title))}`,
    description: `${capitalizeString(normaliseText(data.title))} Icons, Symbols, Pictures, and Images`,
    contentUrl: `${sliderImages[0].src}`,
    copyrightNotice: `© ${new Date().getFullYear()} MindtheGraph. All rights reserved.`,
    width: 900,
    height: 772,
    creator: {
      '@type': 'Organization',
      name: 'MindtheGraph',
    },
    creditText: 'MindtheGraph',
    datePublished: '2023-07-21',
    uploadDate: '2023-07-21',
    license: 'https://trust.editage.com/terms-of-use.html',
  };

  function getTitleForSeo(isTemplatePage, label) {
    if (isTemplatePage) {
      return `Ready to Use Scientific Presentation ${label} and Deck Templates - Mind the Graph`;
    }

    return `Free ${capitalizeString(normaliseText(data.title))} Icons, Symbols, Pictures, and Images | Mind the Graph`;
  }

  function renderAuthView() {
    return (
      <>
        <EmailFormVariant />

        <div>
          <Typography variant="body2">
            Already a member?{' '}
            <Link onClick={() => login({ redirectPage: router.asPath })}>Do Login instead</Link>
          </Typography>
        </div>
      </>
    );
  }

  function renderRelated() {
    return !related ? (
      <IllustrationCategorySection.Loader />
    ) : (
      !isMobile && (
        <RelatedSection>
          <Box py={4}>
            <MainDividerIllustration />
          </Box>
          <RelatedTitle>{relatedTitle}</RelatedTitle>
          <RelatedGrid>
            {related?.map((illustra) => (
              <IllustrationThumb key={illustra.title} {...illustra} />
            ))}
          </RelatedGrid>
        </RelatedSection>
      )
    );
  }

  function renderSlider() {
    return (
      <ImageSlider
        key={uuid()}
        thumbsPosition={sliderThumbsPosition}
        images={sliderImages}
        initialImage={sliderInitialImage}
      />
    );
  }

  return (
    <GalleryLayout category={'template'} label={data.title} tagsData={data} onTagClick={onTagClick}>
      <Head>
        <title>{getTitleForSeo(isTemplatePage, data.title)}</title>
        <meta name="keywords" content={data.tags.join(', ')} />
        <meta
          key="og:description"
          name="description"
          content={`${capitalizeString(normaliseText(data.title))} Icons, Symbols, Pictures, and Images. Customize and download high-quality ${capitalizeString(normaliseText(data.title))} illustrations for your scientific, academic and educational projects.`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaDataImageObject) }}
        />
      </Head>

      <PageTitle>
        {data.title} {t.titleComplement}
      </PageTitle>

      {!isMobile ? (
        <Grid>
          {renderSlider()}

          {!isAuthenticated && (
            <RegisterCard width="400px">
              <RegisterHeader>
                <RegisterHeaderInner>
                  <RegisterTitle>{t.loginForm.login}</RegisterTitle>
                </RegisterHeaderInner>
              </RegisterHeader>

              <Divider />

              {renderAuthView()}
            </RegisterCard>
          )}

          {isAuthenticated && (
            <RegisterCard>
              <Typography align="center">{t.textCardLogged}</Typography>

              <Box mt={2}>
                <img
                  src="/images/use-scientific-illustration-gallery-image.svg"
                  alt="use scientific illustrations image"
                  loading="lazy"
                  width={228}
                  height={168}
                />
              </Box>

              <Box mt={2}>
                <Button onClick={() => router.push('/my-creations')} variant="primary">
                  {t.startCreatingNow}
                </Button>
              </Box>
            </RegisterCard>
          )}
        </Grid>
      ) : (
        renderSlider()
      )}
      {isMobile && (
        <>
          <Box pb={2}>
            <DescriptionIllustration>
              {t.initDescription} <b>{data.title}</b> {t.description}
            </DescriptionIllustration>
          </Box>

          <DescriptionIllustration>
            {t.customizeInit} <b>{data.title}</b> {t.customizeDescription}
          </DescriptionIllustration>

          <Box pt={2}>
            <DescriptionIllustration>{descriptionIllustration}</DescriptionIllustration>
          </Box>

          <MainLoginForMobile>
            <LoginForMobile>
              <RegisterTitleMobile>Join us for free</RegisterTitleMobile>

              <Divider />

              <ButtonSignUpMobile>Sign up for free</ButtonSignUpMobile>

              <DescriptionIllustration>
                Already a member?{' '}
                <Link
                  underline="always"
                  href={`${getEnv('MTG_URL_APP')}${router.basePath}/auth/login-register`}
                >
                  Login
                </Link>{' '}
                instead
              </DescriptionIllustration>
            </LoginForMobile>
          </MainLoginForMobile>
          <MainDividerIllustration />

          <RelatedTitle>{relatedTitle}</RelatedTitle>
          <RelatedGrid>
            {related?.map((illustra) => (
              <IllustrationThumb key={illustra.title} {...illustra} />
            ))}
          </RelatedGrid>
        </>
      )}
      <Box pb={2}>
        <DescriptionIllustration>
          {t.initDescription} <b>{data.title}</b> {t.description}
        </DescriptionIllustration>
      </Box>

      <DescriptionIllustration>
        {t.customizeInit} <b>{data.title}</b> {t.customizeDescription}
      </DescriptionIllustration>

      <Box pt={2}>
        <DescriptionIllustration>{descriptionIllustration}</DescriptionIllustration>
      </Box>

      {renderRelated()}

      <FlagsSelect />
    </GalleryLayout>
  );
}

GalleryDetailPage.propTypes = Props;
GalleryDetailPage.defaultProps = DEFAULT_PROPS;
